import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useSyncExternalStore } from "react";

import { BootstrapProbeMessageSchema } from "../../shared/schemas";
import { appsQuery } from "../api/queries";
import { activeFrames } from "../app-frames";
import { FrameMessageController } from "./frame-message-controller";

interface FrameLifecycleOptions {
  appInstanceId: string;
  intendedPath: string;
  organizationId: string;
  origin: string;
}

interface FrameLifecycleSnapshot {
  launchError: string | null;
  launchKey: number;
  listenerReady: boolean;
}

type SnapshotListener = () => void;

export class FrameLifecycleManager {
  readonly #listeners = new Set<SnapshotListener>();
  readonly #options: FrameLifecycleOptions;
  readonly #queryClient: QueryClient;
  #controller: FrameMessageController | null = null;
  #iframe: HTMLIFrameElement | null = null;
  #snapshot: FrameLifecycleSnapshot = {
    launchError: null,
    launchKey: 0,
    listenerReady: false,
  };

  constructor(options: FrameLifecycleOptions, queryClient: QueryClient) {
    this.#options = options;
    this.#queryClient = queryClient;
  }

  getSnapshot(): FrameLifecycleSnapshot {
    return this.#snapshot;
  }

  subscribe(listener: SnapshotListener): () => void {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  registerFrame(iframe: HTMLIFrameElement | null): void {
    const previousWindow = this.#iframe?.contentWindow;
    if (previousWindow) void this.#controller?.closeFrame(previousWindow);
    this.#iframe = iframe;
    this.#registerActiveFrame();
  }

  registerHost(node: HTMLDivElement | null): (() => void) | undefined {
    if (!node) return undefined;
    let disposed = false;
    const controller = this.#createController();
    this.#controller = controller;
    controller.start();
    queueMicrotask(() => {
      if (!disposed) this.#update({ listenerReady: true });
    });
    return () => {
      disposed = true;
      this.#controller = null;
      void controller.dispose();
    };
  }

  handleLoad(): void {
    const source = this.#registerActiveFrame();
    if (!source) return;
    source.postMessage(
      BootstrapProbeMessageSchema.parse({
        type: "platform:bootstrap-probe",
        appInstanceId: this.#options.appInstanceId,
      }),
      this.#options.origin,
    );
  }

  retry(): void {
    this.#controller?.resetLaunch();
    this.#update({
      launchError: null,
      launchKey: this.#snapshot.launchKey + 1,
    });
  }

  #createController(): FrameMessageController {
    return new FrameMessageController({
      getFrame: () => this.#iframe,
      onCatalogActivity: () => {
        void this.#queryClient.invalidateQueries({
          queryKey: appsQuery(this.#options.organizationId).queryKey,
        });
      },
      onLaunchError: () => {
        this.#update({
          launchError: "This tool could not be opened. Return to your workspace and try again.",
        });
      },
      onSessionExpired: () => {
        this.#update({
          launchError: null,
          launchKey: this.#snapshot.launchKey + 1,
        });
      },
    });
  }

  #registerActiveFrame(): WindowProxy | null {
    const iframe = this.#iframe;
    const source = iframe?.contentWindow;
    if (!iframe || !source) return null;
    activeFrames.set(source, {
      iframe,
      appInstanceId: this.#options.appInstanceId,
      intendedPath: this.#options.intendedPath,
      origin: this.#options.origin,
    });
    return source;
  }

  #update(update: Partial<FrameLifecycleSnapshot>): void {
    this.#snapshot = { ...this.#snapshot, ...update };
    for (const listener of this.#listeners) listener();
  }
}

export function useFrameLifecycle(options: FrameLifecycleOptions) {
  const queryClient = useQueryClient();
  const { appInstanceId, intendedPath, organizationId, origin } = options;
  const manager = useMemo(
    () =>
      new FrameLifecycleManager(
        { appInstanceId, intendedPath, organizationId, origin },
        queryClient,
      ),
    [appInstanceId, intendedPath, organizationId, origin, queryClient],
  );
  const subscribe = useCallback(
    (listener: SnapshotListener) => manager.subscribe(listener),
    [manager],
  );
  const getSnapshot = useCallback(() => manager.getSnapshot(), [manager]);
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return {
    ...snapshot,
    handleLoad: useCallback(() => {
      manager.handleLoad();
    }, [manager]),
    registerFrame: useCallback(
      (node: HTMLIFrameElement | null) => {
        manager.registerFrame(node);
      },
      [manager],
    ),
    registerHost: useCallback(
      (node: HTMLDivElement | null) => manager.registerHost(node),
      [manager],
    ),
    retry: useCallback(() => {
      manager.retry();
    }, [manager]),
  };
}
